import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import InputField from '../../components/formItem/inputField';
import Dropdown from '../../components/dropdown';
import { didimAPI, organizationAPI } from '../../actions/imsServer';
import Popup from '../../components/popup';

const DidimEdit = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const didimData = location.state?.didim;

    const [formData, setFormData] = useState({
        name: '',
        uid: '',
        organizationId: 0,
        organizationName: '',
        info: '',
        note: '',
    });

    const [isSaving, setIsSaving] = useState(false);

    // 팝업 상태
    const [popup, setPopup] = useState({
        open: false,
        message: '',
        subMessage: '',
        onConfirm: null
    });

    // 팝업 표시 함수
    const showPopup = (message, subMessage = '', onConfirm = null) => {
        setPopup({
            open: true,
            message,
            subMessage,
            onConfirm: onConfirm || (() => setPopup(prev => ({ ...prev, open: false })))
        });
    };

    // 팝업 닫기 함수
    const closePopup = () => {
        setPopup(prev => ({ ...prev, open: false }));
    };

    useEffect(() => {
        if (didimData) {
            setFormData(prev => ({
                ...prev,
                name: didimData.name || '',
                uid: didimData.uid || '',
                organizationId: didimData.organizationId || 0,
                organizationName: didimData.organizationName || '',
                info: didimData.info || '',
                note: didimData.info || didimData.note || '',
            }));
        }
    }, [didimData]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleUnlinkOrganization = async () => {
        setFormData(prev => ({ ...prev, organizationId: 0, organizationName: '' }));
        try {
            if (didimData && didimData.id) {
                await didimAPI.updateDidim({
                    id: didimData.id,
                    organizationId: 0,
                });
            }
            showPopup('디딤 연결 해제가 완료되었습니다.');
        } catch (e) {
            console.error('디딤 연결 해제 실패:', e);
            showPopup('디딤 연결 해제에 실패했습니다.', '다시 시도해주세요.');
        }
    };

    const handleCancel = () => {
        navigate('/didims/list');
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (didimData && didimData.id) {
                await didimAPI.updateDidim({
                    id: didimData.id,
                    info: formData.note,
                });
            }
            showPopup('디딤 저장이 완료되었습니다.');
            navigate('/didims');
        } catch (e) {
            console.error('디딤 저장 실패:', e);
            showPopup('디딤 저장에 실패했습니다.', '다시 시도해주세요.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ContentArea sx={{ padding: '20px 0px 0px 0px' }}>
            <Box sx={{ px: 2.5, mb: 3 }}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ width: '20%', fontWeight: 600, backgroundColor: theme.palette.figma.white[10] }}>{t('model_name')} *</TableCell>
                                <TableCell>
                                    <InputField
                                        placeholder={t('please_enter')}
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.figma.white[10] }}>{t('serial')} *</TableCell>
                                <TableCell>
                                    <InputField
                                        placeholder={t('please_enter')}
                                        value={formData.uid}
                                        onChange={(e) => handleChange('uid', e.target.value)}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.figma.white[10] }}>{t('organization_name')}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InputField
                                            placeholder={t('not_connected')}
                                            value={formData.organizationName}
                                            onChange={() => { }}
                                            disabled
                                            showClearIcon={false}
                                            sx={{ flex: 1 }}
                                        />
                                        {formData.organizationName && (
                                            <Button size="small" onClick={handleUnlinkOrganization}>{t('unlink')}</Button>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.figma.white[10] }}>{t('note')}</TableCell>
                                <TableCell>
                                    <InputField
                                        placeholder={t('please_enter')}
                                        value={formData.note}
                                        onChange={(e) => handleChange('note', e.target.value)}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, px: 2.5, pb: 3, alignSelf: 'stretch' }}>
                <Button variant="gray" onClick={handleCancel} sx={{ minWidth: '120px' }}>{t('cancel')}</Button>
                <Button onClick={handleSave} disabled={isSaving || !formData.name || !formData.uid} sx={{ minWidth: '120px' }}>
                    {t('save')}
                </Button>
            </Box>

            {/* 팝업 */}
            <Popup
                open={popup.open}
                message={popup.message}
                subMessage={popup.subMessage}
                onConfirm={popup.onConfirm}
            />
        </ContentArea>
    );
};

export default DidimEdit;


